// src/pages/admin/AdminTownChecklist.js
// The repeatable town launch process. Same steps for every Wave 1A town —
// tracked per town so nothing gets skipped as this scales past Loughrea.

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../lib/firebase';
import { collection, getDocs } from 'firebase/firestore';
import { LAUNCH_CHECKLIST_TEMPLATE, toggleChecklistItem } from '../../lib/db';
import toast from 'react-hot-toast';
import { PageBack } from '../../components/shared/PageBack';

const AdminTownChecklist = () => {
  const navigate = useNavigate();
  const [towns, setTowns] = useState([]);
  const [selectedTown, setSelectedTown] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadTowns = async () => {
    const snap = await getDocs(collection(db, 'towns'));
    const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    setTowns(list);
    if (!selectedTown && list.length > 0) setSelectedTown(list[0].id);
    setLoading(false);
  };

  useEffect(() => { loadTowns(); }, []);

  const currentTown = towns.find(t => t.id === selectedTown);
  const checklist = currentTown?.launchChecklist || {};
  const doneCount = LAUNCH_CHECKLIST_TEMPLATE.filter(item => checklist[item.id]?.done).length;
  const pct = Math.round((doneCount / LAUNCH_CHECKLIST_TEMPLATE.length) * 100);

  const handleToggle = async (itemId, currentlyDone) => {
    await toggleChecklistItem(selectedTown, itemId, !currentlyDone);
    toast.success(!currentlyDone ? 'Marked done' : 'Marked not done');
    loadTowns();
  };

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><div className="spinner" /></div>;

  return (
    <div style={s.page}>
      <button onClick={() => navigate('/admin')} style={s.back}>
        <div style={s.backCircle}>←</div>
        <span style={s.backLbl}>Admin</span>
      </button>
      <div style={s.h1}>Town Launch Checklist</div>
      <p style={s.subtitle}>The same repeatable steps for every town — reuse this for Ballinasloe, Tuam, and beyond.</p>

      {towns.length > 1 && (
        <select style={s.filterSelect} value={selectedTown || ''} onChange={e => setSelectedTown(e.target.value)}>
          {towns.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
      )}

      {currentTown && (
        <>
          <div style={s.progressCard}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ fontWeight: 800, fontSize: 15 }}>{currentTown.name}</span>
              <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--sage-deep)' }}>{doneCount}/{LAUNCH_CHECKLIST_TEMPLATE.length} done</span>
            </div>
            <div style={s.progressTrack}>
              <div style={{ ...s.progressFill, width: `${pct}%` }} />
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 16 }}>
            {LAUNCH_CHECKLIST_TEMPLATE.map(item => {
              const done = checklist[item.id]?.done || false;
              return (
                <button key={item.id} onClick={() => handleToggle(item.id, done)} style={s.itemRow}>
                  <div style={{ ...s.checkbox, background: done ? 'var(--sage-deep)' : 'var(--white)', borderColor: done ? 'var(--sage-deep)' : '#C8D9C9' }}>
                    {done && <span style={{ color: 'white', fontSize: 13 }}>✓</span>}
                  </div>
                  <div style={{ textAlign: 'left', flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink)', textDecoration: done ? 'line-through' : 'none', opacity: done ? 0.6 : 1 }}>
                      {item.label}
                    </div>
                    {item.sub && <div style={{ fontSize: 11.5, color: 'var(--muted)', fontWeight: 500, marginTop: 1 }}>{item.sub}</div>}
                  </div>
                </button>
              );
            })}
          </div>
        </>
      )}
      <PageBack position="bottom" fallbackPath="/admin" />
    </div>
  );
};

const s = {
  page: { padding: '52px 20px 60px', background: 'var(--cream)', minHeight: '100vh' },
  back: { display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', cursor: 'pointer', marginBottom: 20 },
  backCircle: { width: 34, height: 34, background: 'var(--white)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, color: 'var(--sage-dark)', boxShadow: 'var(--shadow-sm)' },
  backLbl: { fontSize: 13, color: 'var(--muted)', fontWeight: 600 },
  h1: { fontSize: 24, fontWeight: 800, color: 'var(--ink)', marginBottom: 4 },
  subtitle: { fontSize: 13, color: 'var(--muted)', marginBottom: 14, lineHeight: 1.5 },
  filterSelect: { width: '100%', padding: '10px 12px', borderRadius: 10, border: '1.5px solid #C8D9C9', fontSize: 13, fontFamily: 'Nunito', background: 'var(--white)', marginBottom: 16, boxSizing: 'border-box' },
  progressCard: { background: 'var(--white)', borderRadius: 16, padding: 16, boxShadow: 'var(--shadow-sm)' },
  progressTrack: { height: 8, background: '#F0F0EE', borderRadius: 100, overflow: 'hidden' },
  progressFill: { height: '100%', background: 'var(--sage-deep)', borderRadius: 100, transition: 'width 0.3s' },
  itemRow: { display: 'flex', alignItems: 'center', gap: 12, background: 'var(--white)', borderRadius: 14, padding: '12px 14px', boxShadow: 'var(--shadow-sm)', border: 'none', cursor: 'pointer', width: '100%', fontFamily: 'Nunito' },
  checkbox: { width: 24, height: 24, borderRadius: 7, border: '2px solid', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
};

export default AdminTownChecklist;
